import React from "react";
import Button from "components/common/ui/buttons/Button/Button";
import Modal from "components/common/modals/Modal/Modal";
import { useSelector } from "react-redux";
import { getIdToken } from "services/auth/auth";
import styled from "styled-components";
import LoadingIndicator from "components/common/ui/LoadingIndicator/LoadingIndicator";
import { RootState } from "state/reducers";
import { shallowEqual } from "state/utilities";
import FeatureRequestsDropdown from "./FeatureRequestsDropdown";
import FeatureRequestInput from "./FeatureRequestInput";
import FeatureRequestFileUpload from "./FeatureRequestFileUpload";

const ModalHeader = styled.h3`
    margin: 0;
    font-size: 24px;
    font-weight: 400;
`;

const ModalParagraph = styled.p`
    font-size: 16px;
    font-weight: 400;
    margin: 0 0 0.5rem 0;
`;

const ModalHeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
`;

const ModalCloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 28px;
    line-height: 1;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 30px;
    height: 30px;

    &:before,
    &:after {
        position: absolute;
        left: 14px;
        top: 2.5px;
        content: " ";
        height: 25px;
        width: 2px;
        background-color: #333;
    }

    &:before {
        transform: rotate(45deg);
    }

    &:after {
        transform: rotate(-45deg);
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const ModalFooterContainer = styled.div`
    margin-top: 1rem;
`;

const RequestError = styled.p`
    color: red;
    margin: 0 0 1.5rem 0;
`;

const priorityOptions = [
    { value: "Matala", label: "Matala" },
    { value: "Keski", label: "Keski" },
    { value: "Korkea", label: "Korkea" },
];

interface FeatureRequestsModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
}

const mapState = (state: RootState) => ({
    token: getIdToken(state),
    selectedOrganizationId: state.common.user.selectedOrganizationId,
});

const FeatureRequestsModal: React.FC<FeatureRequestsModalProps> = ({ isOpen, onRequestClose }) => {
    const { token, selectedOrganizationId } = useSelector(mapState, shallowEqual);

    const [customerTitle, setCustomerTitle] = React.useState("");
    const [customerDescription, setCustomerDescription] = React.useState("");
    const [customerPriority, setCustomerPriority] = React.useState("");
    const [customerBenefits, setCustomerBenefits] = React.useState("");
    const [customerAdditionalInfo, setCustomerAdditionalInfo] = React.useState("");
    const [attachments, setAttachments] = React.useState<File[]>([]);

    const [requestError, setRequestError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [isPriorityDropdownOpen, setIsPriorityDropdownOpen] = React.useState(false);

    const body = {
        customerTitle: customerTitle.trim(),
        customerDescription: customerDescription.trim(),
        customerPriority: customerPriority.trim(),
        customerBenefits: customerBenefits.trim(),
        customerAdditionalInfo: customerAdditionalInfo.trim(),
        selectedOrganizationId,
    };

    const isValid =
        body.customerTitle &&
        body.customerDescription &&
        body.customerPriority &&
        body.customerBenefits;

    React.useEffect(() => {
        if (isOpen) {
            setCustomerTitle("");
            setCustomerDescription("");
            setCustomerPriority("");
            setCustomerBenefits("");
            setCustomerAdditionalInfo("");
            setAttachments([]);
            setRequestError(null);
        }
    }, [isOpen]);

    const handleSubmit = () => {
        setLoading(true);
        setRequestError(null);

        const formData = new FormData();

        formData.append("customerTitle", body.customerTitle);
        formData.append("customerDescription", body.customerDescription);
        formData.append("customerPriority", body.customerPriority);
        formData.append("customerBenefits", body.customerBenefits);
        formData.append("customerAdditionalInfo", body.customerAdditionalInfo);
        formData.append("selectedOrganizationId", selectedOrganizationId || "");

        attachments.forEach((file) => {
            formData.append("attachments", file);
        });

        fetch("/api/feature-requests", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Request failed: ${response.statusText}`);
                } else {
                    onRequestClose();
                }
            })
            .catch((error) => setRequestError(error.message))
            .finally(() => setLoading(false));
    };

    return (
        <Modal isOpen={isOpen} onRequestClose={onRequestClose} disableModalScrolling={true}>
            <ModalHeaderContainer>
                <ModalHeader>Uusi kehitysehdotus</ModalHeader>
                <ModalCloseButton
                    onClick={onRequestClose}
                    disabled={loading}
                    aria-label="Sulje ikkuna"
                />
            </ModalHeaderContainer>
            <ModalParagraph>Tähdellä merkityt tiedot ovat pakollisia*</ModalParagraph>
            <FeatureRequestInput
                label="Kehitysehdotuksen otsikko*"
                required={true}
                value={customerTitle}
                onChange={setCustomerTitle}
                disabled={loading}
            />
            <FeatureRequestInput
                label="Lyhyt kuvaus*"
                required={true}
                value={customerDescription}
                onChange={setCustomerDescription}
                multiline={true}
                disabled={loading}
            />
            <FeatureRequestsDropdown
                label="Prioriteetti*"
                isOpen={isPriorityDropdownOpen}
                toggle={setIsPriorityDropdownOpen}
                options={priorityOptions}
                selectedValue={customerPriority}
                onValueChange={setCustomerPriority}
                placeholder="Valitse prioriteetti"
                disabled={loading}
                fullWidth={true}
            />
            <FeatureRequestInput
                label="Kuvaile kehitysehdotuksen toteutumisen hyötyjä organisaatiossasi*"
                required={true}
                value={customerBenefits}
                onChange={setCustomerBenefits}
                multiline={true}
                disabled={loading}
            />
            <FeatureRequestInput
                label="Lisätiedot"
                required={false}
                value={customerAdditionalInfo}
                onChange={setCustomerAdditionalInfo}
                multiline={true}
                disabled={loading}
            />
            <FeatureRequestFileUpload
                label="Liitteet (valinnainen)"
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                disabled={loading}
            />
            <ModalFooterContainer>
                {requestError && <RequestError>{requestError}</RequestError>}
                <Button onClick={handleSubmit} disabled={!isValid || loading}>
                    Lähetä
                </Button>
            </ModalFooterContainer>
            <LoadingIndicator isLoading={loading} />
        </Modal>
    );
};

export default FeatureRequestsModal;
